import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Loader2 } from 'lucide-react';

interface ApiBrand {
  id: number;
  name: string;
  logo?: string | null;
}

interface CreateProjectModalProps {
  onClose: () => void;
  onCreated: (projectId: number) => void;
}

export function CreateProjectModal({ onClose, onCreated }: CreateProjectModalProps) {
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [brandId, setBrandId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data: brands = [] } = useQuery<ApiBrand[]>({
    queryKey: ['/api/brands'],
    staleTime: 60000,
  });

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || !brandId || isCreating) return;
    setIsCreating(true);
    try {
      const resp = await fetch('/api/canvas/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          brandId: brandId || null,
          instructions: instructions.trim() || null,
          isFolder: true,
        }),
      });
      if (resp.ok) {
        const project = await resp.json();
        onCreated(project.id);
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(2px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white rounded-2xl overflow-hidden"
        style={{ width: 480, maxWidth: 'calc(100vw - 32px)', boxShadow: '0 8px 40px rgba(0,0,0,0.14)', border: '1px solid #E5E2DC' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between"
          style={{ padding: '20px 24px 16px', borderBottom: '1px solid #F0EDE8' }}
        >
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A' }}>New project</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded-lg transition-colors"
            style={{ width: 28, height: 28, color: '#8C8578' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {/* Name */}
          <div className="mb-4">
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#8C8578', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Project name
            </label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              placeholder="e.g. Summer Campaign 2026"
              className="w-full rounded-xl px-4 py-2.5 outline-none transition-colors"
              style={{ fontSize: 14, color: '#1A1A1A', border: '1px solid #DCDCDC', background: '#FAFAF9' }}
              onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = '#A8A49E'; }}
              onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = '#DCDCDC'; }}
            />
          </div>

          {/* Instructions */}
          <div className="mb-4">
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#8C8578', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Instructions <span style={{ textTransform: 'none', fontWeight: 400, color: '#C8C4BE' }}>(optional)</span>
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Describe the goal or context for this project..."
              rows={3}
              className="w-full rounded-xl px-4 py-2.5 resize-none outline-none transition-colors"
              style={{ fontSize: 14, color: '#1A1A1A', border: '1px solid #DCDCDC', background: '#FAFAF9', lineHeight: 1.6 }}
              onFocus={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#A8A49E'; }}
              onBlur={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#DCDCDC'; }}
            />
          </div>

          {/* Brand */}
          <div className="mb-2">
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#8C8578', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Brand
            </label>
            <select
              value={brandId ?? ''}
              onChange={(e) => setBrandId(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-xl px-4 py-2.5 outline-none transition-colors"
              style={{ fontSize: 14, color: brandId ? '#1A1A1A' : '#B5B0A8', border: brandId ? '1px solid #DCDCDC' : '1px solid #E0A0A0', background: '#FAFAF9', appearance: 'none' }}
            >
              <option value="">Select a brand…</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            {!brandId && (
              <p style={{ fontSize: 11, color: '#C8A0A0', marginTop: 4 }}>A brand is required to create a project.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3"
          style={{ padding: '12px 24px 20px' }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-colors"
            style={{ color: '#8C8578', background: 'transparent' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || !brandId || isCreating}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: name.trim() && brandId ? '#1A1A1A' : '#E5E2DC',
              color: name.trim() && brandId ? '#FFFFFF' : '#B5B0A8',
              cursor: name.trim() && brandId ? 'pointer' : 'not-allowed',
            }}
          >
            {isCreating && <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />}
            {isCreating ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </div>
    </div>
  );
}
